import { faChevronDown, faChevronUp, faRefresh, faTimeline } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { EXPLAIN_CHARTING, SCAN_MULTI } from "../../../../amplify/appsync/write";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { SectionChartingButton } from "./SectionChartingButton";
import { SectionChartingDetails } from "./SectionChartingDetails";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { isSectionUnread } from "../utils";
import { dashboardLLMCache } from "../cache";

export const SectionCharting = ({
    className='',
    isLoading,
    data,
    active,
    setActive,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    tenant,
    setRegenerateTarget,
    setShowContextMenu,
    handleShowScanMultiModal,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_CHARTING]) {
            const version = latestFileVersions[EXPLAIN_CHARTING];

            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
            .then((data: any) => {
                // console.log("[Patient Summary] Found citation_mapping", data.citation_mapping);
                setScanMulti(data);
            })
        }
    }, [latestFileVersions]);

    const titles = [
        "Workup",
        "Management",
        "Disposition",
        "Records Reviewed",
        "Escalation",
        "Drugs Considered",
        "Tests Considered",
        "Critical Care Timeline",
    ];

    const titleToSection: { [key: string]: any } = {
        "Workup": "workup",
        "Management": "management",
        "Disposition": "disposition",
        "Records Reviewed": "discussion",
        "Escalation": "escalation",
        "Drugs Considered": "drugs_considered",
        "Tests Considered": "tests_considered",
        "Critical Care Timeline": "critical_care_time",
    };

    const textColors = [
        "blue-text",
        "green-text",
        "red-text",
        "purple-text",
        "orange-text",
    ];

    if (!isLoading && (!data || data["error"])) {
        return (
            <GenerateModuleHeroV2 type={EXPLAIN_CHARTING} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} />
        )
    }

    return (
        <Card className={`${className}`}>
            <FakeLoadingBar isActive={isLoading} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faTimeline} fixedWidth /></span><strong>Charting</strong> {isSectionUnread(EXPLAIN_CHARTING, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={"explain_charting"} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />

                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {e.stopPropagation(); setRegenerateTarget(EXPLAIN_CHARTING); setShowContextMenu(true)}}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"charting"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        {
                            data && (
                                <Row className="mt-2 fade-in">
                                    <Col md={6}>
                                        {
                                            titles.map((title: string, idx: number) => (
                                                <SectionChartingButton key={title} idx={idx} isLoading={false} active={active} setActive={setActive} data={data[titleToSection[title]]} title={title} color={textColors[idx % textColors.length]} scanMulti={scanMulti} />
                                            ))
                                        }
                                    </Col>
                                    <Col md={6}>
                                        {
                                            data.reasoned_timeline && (
                                                <SectionChartingDetails data={data.reasoned_timeline ?? []} activeKey={titleToSection[titles[active]]} scanMulti={scanMulti} />
                                            )
                                        }
                                    </Col>
                                </Row>
                            )
                        }
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
